import React, {
  ChangeEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import FullCalendar, {
  EventInput,
  EventClickArg,
  EventContentArg,
  EventMountArg,
  DatesSetArg,
  DayCellContentArg,
  DayHeaderContentArg,
} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import luxonPlugin, { toLuxonDateTime } from '@fullcalendar/luxon';
import css from './MerchantWidgetCalendar.module.scss';
import BookingEvent, {
  FullCalendarViewStyle as ViewStyle,
} from '../../../domain/entities/BookingEvent';
import {
  VacancyStatus,
  VacancyStatusEnum,
} from '../../../domain/entities/VacancyStatus';
import implFullCalendarRepository from '../../../infrastructure/repositories/FullCalendarEventRepository';
import MerchantFullCalendarEventContent from './pc/MerchantFullCalendarEventContent';

import { SelectBox } from '../../common/SelectBox';
import {
  useBookingCalendarMeta,
  useStaffBookingPageMapping,
} from '../../../hooks/api/booking-calendar-meta';
import {
  mapToCalendarEvents,
  useFilterBookingPagesCalendar,
  useFormatEventTitle,
} from '../../../hooks/fullcalendar';
import { MerchantBusinessHour } from '../../../interfaces/merchants';
import { CalendarLoading } from './CalendarLoading';
import {
  nextFetchEndDateFC,
  nextFetchStartDate,
  yesterday,
} from './calendarHelper';

// ================
// WidgetCalendar types
// ================

const ViewStyles = [
  ViewStyle.dayGridMonth,
  ViewStyle.timeGridWeek,
  ViewStyle.timeGridThreeDay,
  ViewStyle.timeGridDay,
  ViewStyle.listMonth,
];

// ================
// MerchantWidgetCalendar
// ================

const intlPref = 'merchants.services.merchantFullCalendar.calendar.';

export interface ComponentProps {
  merchantPublicId: string;
  merchantTimeZone: string;
  baseDate: string;
  business_hours: MerchantBusinessHour[];
  calendarViewStyle: string;
  ga?: string;
  gl?: string;
  servicePublicIds?: string[];
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  ga_session_id?: string;
  ga_client_id?: string;
}

type Props = ComponentProps;

const getEventContent = (arg: EventContentArg) => {
  const bookingEvent = arg.event.extendedProps.bookingEvent as BookingEvent;
  const vacancyStatus: VacancyStatus = new VacancyStatus({
    vacancyCount: bookingEvent.vacancy,
    waitingListProvided: bookingEvent.isWaitingListProvided,
    full: bookingEvent.full,
    isRegisteredWaitingList: bookingEvent.isRegisteredWaitingList,
  });

  if (vacancyStatus.vacancyAppearance === VacancyStatusEnum.SOLD_OUT) {
    return (
      <MerchantFullCalendarEventContent
        bookingEvent={bookingEvent}
        viewType={arg.view.type}
        textColor={arg.event.backgroundColor}
        title={arg.event.title}
        timeText={arg.timeText}
      />
    );
  }

  // render default component
  return undefined;
};

const formatDayCellContent = (e: DayCellContentArg) => {
  return (e.dayNumberText = e.dayNumberText.replace('日', ''));
};

const formatDayHeaderContent = (e: DayHeaderContentArg) => {
  return (e.text = e.text.replace('(', ' ').replace(')', ''));
};

// ほぼ MerchantFullCalendarと同様の内容である。 hooksに切り出して共通部分まとめるなどすると良さそう
const MerchantWidgetCalendar: React.FC<Props> = ({
  merchantTimeZone,
  merchantPublicId,
  baseDate,
  servicePublicIds,
  ga,
  gl,
  business_hours,
  calendarViewStyle,
  utm_campaign,
  utm_medium,
  utm_source,
  ga_session_id,
  ga_client_id,
}) => {
  const intl = useIntl();
  const [events, setEvents] = useState<EventInput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchUntil, setFetchUntil] = useState<string>(
    yesterday(merchantTimeZone).toISO(),
  );
  const [selectedBookingPageId, setSelectedBookingPageId] = useState<
    string | undefined
  >(undefined);

  const { data } = useBookingCalendarMeta(merchantPublicId);
  const bookingPages = data?.booking_pages;

  const [bookingPageIdsByStaff, setBookingPageIdsByStaff] = useState<
    string[] | undefined
  >(undefined);

  const staffFilter = useFilterBookingPagesCalendar(bookingPageIdsByStaff);

  const bookingPageFilter = useFilterBookingPagesCalendar(
    selectedBookingPageId ? [selectedBookingPageId] : undefined,
  );

  const providedEvents = useMemo<EventInput[]>(() => {
    const filteredByStaff = staffFilter(events);
    const result = bookingPageFilter(filteredByStaff);

    return result;
  }, [bookingPageFilter, staffFilter, events]);

  const selectFilterBookingPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const targetId = e.target.value;
    setSelectedBookingPageId(targetId);
  };

  const { staffMembers, staffBookingPageIdsMap } = useStaffBookingPageMapping(
    merchantPublicId,
    {
      bookingPageIds: servicePublicIds,
    },
  );

  const fetchServices = useCallback<
    (params: { startStr: string; endStr: string }) => void
  >(
    ({ startStr, endStr }) => {
      setIsLoading(true);

      const searchEvents = implFullCalendarRepository.SearchWidget(
        merchantPublicId,
        startStr,
        endStr,
        servicePublicIds,
      );

      searchEvents
        .then((result) => {
          setEvents((events) => {
            return [...events, ...mapToCalendarEvents(result.events)];
          });
          setFetchUntil(endStr);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [merchantPublicId, servicePublicIds],
  );

  const formatEventTitle = useFormatEventTitle(intl);

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      if (arg.view.type === 'dayGridMonth') {
        arg.view.calendar.setOption('height', 'auto');
      } else {
        arg.view.calendar.setOption('height', 700);
      }
      const newFetchUntil = nextFetchStartDate(fetchUntil);
      const end = nextFetchEndDateFC(
        toLuxonDateTime(arg.end, arg.view.calendar),
      );

      if (end > newFetchUntil) {
        fetchServices({
          startStr: newFetchUntil.toISO(),
          endStr: end.toISO(),
        });
      }
    },
    [fetchUntil, fetchServices],
  );

  const onSelectStaff = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    (e) => {
      e.preventDefault();
      const staffId = e.target.value;
      if (staffId) {
        const selectedStaffAssignedBookingPageIds =
          staffBookingPageIdsMap[staffId];

        setBookingPageIdsByStaff(selectedStaffAssignedBookingPageIds);
      } else {
        setBookingPageIdsByStaff(undefined);
      }
    },
    [staffBookingPageIdsMap],
  );

  const handleEventDidMount = useCallback(
    (arg: EventMountArg) => {
      const bookingEvent = arg.event.extendedProps.bookingEvent as BookingEvent;
      const isShorten = arg.view.type !== ViewStyle.listMonth;
      arg.event.setProp('title', formatEventTitle(bookingEvent, isShorten));
    },
    [formatEventTitle],
  );

  const handleEventClick = (arg: EventClickArg) => {
    const event = arg.event.extendedProps.bookingEvent as BookingEvent;
    const params = new URLSearchParams();

    if (ga) {
      params.append('_ga', ga);
    }

    if (gl) {
      params.append('_gl', gl);
    }

    if (utm_source && utm_medium && utm_campaign) {
      params.append('utm_source', utm_source);
      params.append('utm_medium', utm_medium);
      params.append('utm_campaign', utm_campaign);
    }

    if (ga_session_id && ga_client_id) {
      params.append('gaClientId', ga_client_id);
      params.append('gaSessionId', ga_session_id);
    }

    if (params.size > 0) {
      window.open(`${event.bookingActionUrl}&${params}`);
    } else {
      window.open(event.bookingActionUrl);
    }
  };

  return (
    <div className={css.MerchantFullCalendar}>
      <div className={css.MerchantFullCalendar__Calendar}>
        {!servicePublicIds && (
          <SelectBox onChange={selectFilterBookingPage}>
            <option value={''}>
              {intl.formatMessage({ id: intlPref + 'allBookingPages' })}
            </option>
            {bookingPages &&
              bookingPages.map((bookingPage) => (
                <option
                  key={bookingPage.public_id}
                  value={bookingPage.public_id}
                >
                  {bookingPage.name}
                </option>
              ))}
          </SelectBox>
        )}
        {staffMembers.length > 0 && (
          <SelectBox onChange={onSelectStaff}>
            <option value="">
              {intl.formatMessage({ id: intlPref + 'assignedStaffMembers' })}
            </option>
            {staffMembers.map((staff) => {
              return (
                <option key={staff.canonical_id} value={staff.canonical_id}>
                  {staff.name}
                </option>
              );
            })}
          </SelectBox>
        )}
        <FullCalendar
          aspectRatio={1.3}
          handleWindowResize={true}
          expandRows={true}
          allDayText={intl.formatMessage({
            id: intlPref + 'buttonText.allDay',
          })}
          businessHours={{
            startTime: '00:00',
            endTime: '24:00',
            daysOfWeek: business_hours
              .filter((b1) => b1.is_open)
              .map((b2) => {
                return b2.weekday;
              }),
          }}
          buttonText={{
            today: intl.formatMessage({
              id: intlPref + 'buttonText.today',
            }),
            month: intl.formatMessage({
              id: intlPref + 'buttonText.month',
            }),
            week: intl.formatMessage({
              id: intlPref + 'buttonText.week',
            }),
            day: intl.formatMessage({ id: intlPref + 'buttonText.day' }),
            list: intl.formatMessage({
              id: intlPref + 'buttonText.list',
            }),
          }}
          slotLabelFormat={'H:mm'}
          nowIndicator={true}
          datesSet={handleDatesSet}
          editable={false}
          eventClick={handleEventClick}
          eventContent={getEventContent}
          eventDidMount={handleEventDidMount}
          eventDisplay={'block'}
          events={providedEvents}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: false,
            hour12: false,
          }}
          fixedWeekCount={false}
          headerToolbar={{
            left: 'today',
            center: 'prev title next',
            right: ViewStyles.join(),
          }}
          initialDate={baseDate}
          initialView={calendarViewStyle}
          locale={intl.locale}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, luxonPlugin]}
          timeZone={merchantTimeZone}
          views={{
            timeGridThreeDay: {
              type: 'timeGrid',
              duration: { days: 3 },
              buttonText: intl.formatMessage({
                id: intlPref + 'buttonText.3days',
              }),
            },
          }}
          dayCellContent={formatDayCellContent}
          dayHeaderContent={formatDayHeaderContent}
        />
        {isLoading && <CalendarLoading />}
      </div>
    </div>
  );
};

export default MerchantWidgetCalendar;
